/** TASK-329 / TASK-567 — client-side platform fee estimate (matches BE half-up cents). */

const DEFAULT_RATE = 0.03;
const rateCache = new Map();

export async function fetchPlatformFeeRate(apiBase, shopId = null) {
  const cacheKey = shopId ? `shop:${shopId}` : "global";
  if (rateCache.has(cacheKey)) {
    return rateCache.get(cacheKey);
  }

  try {
    const url = shopId
      ? `${apiBase}/api/config/platform-fee-rate?shopId=${encodeURIComponent(shopId)}`
      : `${apiBase}/api/config/platform-fee-rate`;
    const res = await fetch(url);
    if (res.ok) {
      const data = await res.json();
      let rate = Number(data.rate);
      if (!Number.isFinite(rate) || rate < 0) {
        rate = DEFAULT_RATE;
      }
      rateCache.set(cacheKey, rate);
      return rate;
    }
  } catch {
    /* use default */
  }

  rateCache.set(cacheKey, DEFAULT_RATE);
  return DEFAULT_RATE;
}

export function clearPlatformFeeRateCache(shopId = null) {
  if (shopId) {
    rateCache.delete(`shop:${shopId}`);
  } else {
    rateCache.clear();
  }
}

export async function fetchPlatformFeeRateNotice(apiBase, shopId) {
  if (!shopId) return { required: false };

  const res = await fetch(
    `${apiBase}/api/config/platform-fee-rate-notice?shopId=${encodeURIComponent(shopId)}`,
    { credentials: "include" },
  );

  if (!res.ok) {
    return { required: false };
  }

  return res.json();
}

export async function acknowledgePlatformFeeRate(apiBase, shopId, versionId) {
  const res = await fetch(`${apiBase}/api/config/platform-fee-rate-acknowledge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ shopId, versionId }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Failed to acknowledge rate change");
  }

  clearPlatformFeeRateCache(shopId);
  return data;
}

export function estimatePlatformFeeBreakdown(quoteAmount, rate = DEFAULT_RATE) {
  const subtotal = Number(quoteAmount);
  if (!Number.isFinite(subtotal) || subtotal <= 0) {
    return null;
  }
  const platformFee = Math.round(subtotal * rate * 100) / 100;
  const shopNet = Math.round((subtotal - platformFee) * 100) / 100;
  return {
    customerQuote: subtotal,
    platformFee,
    shopNet,
    rate,
  };
}

export function formatUsd(amount) {
  return `$${Number(amount).toFixed(2)}`;
}

export function formatRatePercent(rate) {
  return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
}

export function formatRateEffectiveDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
